/**
 * occt-cam-kontur — GERÇEK BREP CAM sınır çıkarma (OpenCascade).
 *
 * Import edilen STEP'i OCCT'ye okur (parça userData['__stepBytes']), seçilen
 * yüzün EXACT sınırını (yuzKonturBrep: sıralı LINE/ARC + merkez/yarıçap) çıkarır.
 * Mesh/tessellation yaklaşımı DEĞİL — gerçek topoloji. Kare→düz, daire→gerçek yay.
 *
 * Doğrulandı (tarayıcı): kutu 100×80 → exact 4 köşe düz; silindir → Circle r=20 merkez exact.
 *
 * GÜVENLİK: faceId (occt-import mesh) ↔ OCCT TopExp sıra eşlemesi garanti değil.
 * Çıkan kontur tıklanan pick noktasına yakın değilse null döner (çağıran mesh'e fallback).
 */

import { fikoOCCT } from '../../fiko-modelleme/fiko-occt-kernel';

export interface IDunyaNokta { x: number; y: number; z: number; }

const shapeCache = new WeakMap<object, any>();

/** Bir yay segmentini (merkez+yarıçap+uçlar) düzleminde noktalara böler. */
function yayTessela3D(a: IDunyaNokta, b: IDunyaNokta, arc: { cx: number; cy: number; cz: number; r: number }, n = 32): IDunyaNokta[] {
    const ax = a.x - arc.cx, ay = a.y - arc.cy, az = a.z - arc.cz;
    const bx = b.x - arc.cx, by = b.y - arc.cy, bz = b.z - arc.cz;
    // düzlem normali (ra × rb); a≈b ise tam çember → XY varsayımı
    let nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
    let nl = Math.hypot(nx, ny, nz);
    const tamCember = nl < 1e-6;
    if (tamCember) { nx = 0; ny = 0; nz = 1; nl = 1; }
    nx /= nl; ny /= nl; nz /= nl;
    const ul = Math.hypot(ax, ay, az) || 1;
    const ux = ax / ul, uy = ay / ul, uz = az / ul;        // radyal taban
    const vx = ny * uz - nz * uy, vy = nz * ux - nx * uz, vz = nx * uy - ny * ux; // dik taban (n×u)
    let ang: number;
    if (tamCember) ang = 2 * Math.PI;
    else { const bu = bx * ux + by * uy + bz * uz, bv = bx * vx + by * vy + bz * vz; ang = Math.atan2(bv, bu); if (ang < 0) ang += 2 * Math.PI; }
    const adim = Math.max(8, Math.ceil(ang / (Math.PI / 18))); // ~10°
    const pts: IDunyaNokta[] = [];
    for (let k = 1; k <= adim; k++) {
        const t = ang * (k / adim), c = Math.cos(t), s = Math.sin(t);
        pts.push({ x: arc.cx + arc.r * (ux * c + vx * s), y: arc.cy + arc.r * (uy * c + vy * s), z: arc.cz + arc.r * (uz * c + vz * s) });
    }
    return pts;
}

/**
 * Seçilen yüzün EXACT BREP konturunu MODEL-uzayı noktaları olarak döndürür (yaylar tessela).
 * Çağıran (occtFaceVertices) bunu mesh matrixWorld ile world'e çevirip pick-guard uygular.
 */
export async function occtYuzKonturu(
    grup: any, faceId: number
): Promise<IDunyaNokta[] | null> {
    try {
        const bytes = grup?.userData?.['__stepBytes'];
        if (!bytes || faceId == null || faceId < 0) return null;

        let shape = shapeCache.get(grup);
        if (!shape) { shape = await fikoOCCT.stepOku(bytes); if (shape) shapeCache.set(grup, shape); }
        if (!shape) return null;

        const kontur = fikoOCCT.yuzKonturBrep(shape, faceId);
        if (!kontur || !kontur.segmentler?.length) return null;

        // Segmentleri sıralı model noktalarına aç (yay→tessela)
        const noktalar = segmentleriNoktalara(kontur.segmentler);
        if (noktalar.length < 3) return null;
        return noktalar; // model-uzayı; çağıran world'e çevirip guard uygular
    } catch (e) {
        console.warn('[OCCT-CAM] occtYuzKonturu hata, fallback:', e);
        return null;
    }
}

/** Segment dizisini (LINE/ARC) sıralı model noktalarına açar (yay tessela). */
function segmentleriNoktalara(segmentler: any[]): IDunyaNokta[] {
    const noktalar: IDunyaNokta[] = [];
    const eps = 1e-4;
    const ekle = (p: IDunyaNokta) => {
        const s = noktalar[noktalar.length - 1];
        if (s && Math.abs(s.x - p.x) < eps && Math.abs(s.y - p.y) < eps && Math.abs(s.z - p.z) < eps) return;
        noktalar.push(p);
    };
    for (const seg of segmentler) {
        const ps = seg.noktalar;
        if (!ps?.length) continue;
        if (seg.tip === 'arc' && seg.arc && ps.length >= 2) {
            ekle(ps[0]);
            for (const q of yayTessela3D(ps[0], ps[ps.length - 1], seg.arc)) ekle(q);
        } else for (const q of ps) ekle(q);
    }
    return noktalar;
}

/**
 * SENKRON exact offset: yüzün dış konturunu delta kadar OCCT ile offset'ler (yay-korumalı).
 * Shape ZATEN cache'liyse (pick'te yüklendi) await gerektirmez → sync pipeline'da kullanılır.
 * Cache'te yoksa null (çağıran Clipper'a düşer). MODEL-uzayı nokta döndürür.
 */
export function occtKonturOffsetSync(grup: any, faceId: number, delta: number, sharp = true): IDunyaNokta[] | null {
    try {
        if (!grup || faceId == null || faceId < 0) return null;
        const shape = shapeCache.get(grup);
        if (!shape) return null; // henüz yüklenmemiş → Clipper fallback
        const kontur = fikoOCCT.yuzKonturOffset(shape, faceId, delta, sharp);
        if (!kontur?.segmentler?.length) return null;
        const pts = segmentleriNoktalara(kontur.segmentler);
        return pts.length >= 3 ? pts : null;
    } catch {
        return null;
    }
}

export interface IModelSeg { tip: 'line' | 'arc' | 'egri'; noktalar: IDunyaNokta[]; arc?: { cx: number; cy: number; cz: number; r: number; cw: boolean }; }

/** SENKRON exact offset — SEGMENT (LINE/ARC) döndürür (G02/G03 için arc korunur). Cache'li shape gerekir. */
export function occtKonturOffsetSegSync(grup: any, faceId: number, delta: number, sharp = true): IModelSeg[] | null {
    try {
        if (!grup || faceId == null || faceId < 0) return null;
        const shape = shapeCache.get(grup);
        if (!shape) return null;
        const kontur = fikoOCCT.yuzKonturOffset(shape, faceId, delta, sharp);
        return kontur?.segmentler?.length ? (kontur.segmentler as IModelSeg[]) : null;
    } catch {
        return null;
    }
}

/** Seçilen yüzün iç konturlarını (delik/ada) MODEL-uzayı nokta-loop'ları olarak döndürür. */
export async function occtYuzAdalar(grup: any, faceId: number): Promise<IDunyaNokta[][] | null> {
    try {
        const bytes = grup?.userData?.['__stepBytes'];
        if (!bytes || faceId == null || faceId < 0) return null;
        let shape = shapeCache.get(grup);
        if (!shape) { shape = await fikoOCCT.stepOku(bytes); if (shape) shapeCache.set(grup, shape); }
        if (!shape) return null;
        const adaSeg = fikoOCCT.yuzAdalar(shape, faceId);
        if (!adaSeg?.length) return null;
        const adalar = adaSeg.map(seg => segmentleriNoktalara(seg)).filter(a => a.length >= 3);
        return adalar.length ? adalar : null;
    } catch (e) {
        console.warn('[OCCT-CAM] occtYuzAdalar hata:', e);
        return null;
    }
}
